import React from 'react';
import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://www.onlinebusiness.co.il';
const DEFAULT_IMAGE = `${BASE_URL}/og-image.png`;
const SITE_NAME = 'המרכז הישראלי לעצמאות דיגיטלית';
// Generic fallback description ONLY. Every page should pass its own — SEO
// audit specifically flags duplicate descriptions across pages.
const DEFAULT_DESCRIPTION =
  'הפכו את הידע שלכם בשיווק דיגיטלי לנכסים מניבים. מועדון משווקים מקצועי, הצעות אפיליאייט בלעדיות, וליווי אישי לבניית הכנסה עצמאית.';

/** OG/Twitter crawlers require absolute URLs. Promote relative paths (e.g.
 *  uploaded hero images served from `/api/static/...`) to the production host. */
const absolutize = (url) => {
  if (!url) return DEFAULT_IMAGE;
  if (/^https?:\/\//i.test(url)) return url;
  return `${BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

/** Enforce Google's ~60-char title cap while adding the site-name suffix when
 *  it still fits. For long page titles we drop the suffix rather than crop the
 *  page-specific portion — a truncated brand name is worse than no brand name. */
const buildTitle = (pageTitle) => {
  if (!pageTitle) return SITE_NAME;
  const suffix = ` | ${SITE_NAME}`;
  const MAX = 60;
  // If page title alone is already at/over budget, use it as-is (truncated).
  if (pageTitle.length >= MAX - 4) {
    return pageTitle.length > MAX
      ? `${pageTitle.slice(0, MAX - 1).trimEnd()}…`
      : pageTitle;
  }
  // If page title + suffix fits → use both. Otherwise → drop the suffix.
  return (pageTitle.length + suffix.length) <= MAX
    ? `${pageTitle}${suffix}`
    : pageTitle;
};

const SEO = ({
  title,
  description,
  path = '/',
  image,
  type = 'website',
  /** Set to true for internal/duplicate/thank-you/staging pages that should
   *  NOT appear in Google. Uses `noindex,follow` so link equity still flows
   *  to canonical pages. */
  noindex = false,
}) => {
  const fullUrl = `${BASE_URL}${path}`;
  const fullTitle = buildTitle(title);
  const finalDescription = description && description.trim() ? description.trim() : DEFAULT_DESCRIPTION;
  const fullImage = absolutize(image);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={finalDescription} />
      {noindex && <meta name="robots" content="noindex,follow" />}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={title || SITE_NAME} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="he_IL" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title || SITE_NAME} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={fullImage} />
      <link rel="canonical" href={fullUrl} />
    </Helmet>
  );
};

export default SEO;
